import { type ReactElement } from 'react';
import { NoImageIcon } from '../icons/NoImageIcon';
import { ReloadIcon } from '../icons/ReloadIcon';
import { MultiplyIcon } from '../icons/MultiplyIcon';
import DotsLoader from '../DotsLoader';
import Tooltip from '../Tooltip';
import { ATTACHMENT_TILE_CLASS_NAME, ATTACHMENT_TILE_REMOVE_CLASS_NAME } from './classnames';
import { getLoaderSizePx, StyledErrorLabel, StyledIconSlot, StyledPendingOverlay, StyledPreviewImage, StyledRemoveButton, StyledRetryButton, StyledThumb, StyledThumbButton, StyledTile } from './styles';
import { type AttachmentTileProps } from './types';
const DEFAULT_RETRY_ARIA_LABEL = 'Retry';

/**
 * One attached file as a square tile, for a composer row or a sent message.
 *
 * The tile carries no filename by design — the name lives in the tooltip — so
 * a file that has a picture has to show it for the row to be readable at all.
 * `previewUrl` is whatever the consumer can produce cheaply (a local blob URL
 * while uploading, a remote one afterwards); `icon` is the fallback when there
 * is no preview, and the permanent state for file types with nothing to show.
 *
 * The component owns presentation only. Uploading, signing URLs, retrying and
 * removing all stay with the consumer, which passes the resulting `status` back
 * in. Every piece of text is a prop, so the tile carries no copy of its own.
 */
const AttachmentTile = ({
  filename,
  icon,
  unavailableIcon = <NoImageIcon />,
  unavailableTooltip,
  errorTooltip,
  errorLabel,
  previewUrl = null,
  size = 'default',
  status = 'success',
  isUnavailable = false,
  onRemove,
  onRetry,
  onPreviewError,
  onActivate,
  activateAriaLabel,
  removeAriaLabel,
  retryAriaLabel,
  dataTestId
}: AttachmentTileProps): ReactElement => {
  const isError = status === 'error';
  const isPending = status === 'uploading' || status === 'loading';
  const showPreview = !isError && !isUnavailable && previewUrl != null && previewUrl.length > 0;
  // A tile with nothing to show full size is not worth a click, and the error
  // tile already owns its own retry button.
  const isActivatable = onActivate != null && showPreview && !isPending;
  const tooltipText = isUnavailable && unavailableTooltip != null ? unavailableTooltip : isError && errorTooltip != null ? errorTooltip : filename;
  const renderThumbGraphic = (): ReactElement => {
    if (isError) {
      return <>
          {onRetry != null ? <StyledRetryButton variant="tonal" size="small" onClick={onRetry} leftIcon={<ReloadIcon />} aria-label={retryAriaLabel ?? DEFAULT_RETRY_ARIA_LABEL} /> : null}
          {errorLabel != null ? <StyledErrorLabel>{errorLabel}</StyledErrorLabel> : null}
        </>;
    }
    if (isUnavailable) {
      return <StyledIconSlot isPending={false}>{unavailableIcon}</StyledIconSlot>;
    }
    if (showPreview && previewUrl != null) {
      return <StyledPreviewImage src={previewUrl} alt={filename} onError={onPreviewError} />;
    }
    return <StyledIconSlot isPending={isPending}>{icon}</StyledIconSlot>;
  };
  const thumbContent = <>
      {renderThumbGraphic()}
      {isPending ? <StyledPendingOverlay>
          {/* `surface="brand"` renders the static-white variant — the overlay is a
              fixed dark scrim now, so the default brand-blue loader would have too
              little contrast against it. */}
          <DotsLoader surface="brand" {...getLoaderSizePx(size)} />
        </StyledPendingOverlay> : null}
    </>;
  return <Tooltip kind="singleline" title={tooltipText} disabled={tooltipText === ''} shouldDelay>
      <StyledTile className={ATTACHMENT_TILE_CLASS_NAME} data-testid={dataTestId} isError={isError} size={size}>
        {isActivatable ? <StyledThumbButton type="button" onClick={onActivate} aria-label={activateAriaLabel ?? filename} isError={isError} isUnavailable={isUnavailable} size={size}>
            {thumbContent}
          </StyledThumbButton> : <StyledThumb isError={isError} isUnavailable={isUnavailable} size={size}>
            {thumbContent}
          </StyledThumb>}
        {onRemove != null && !isPending ? <StyledRemoveButton variant="tonal" size="small" className={ATTACHMENT_TILE_REMOVE_CLASS_NAME} onClick={onRemove} leftIcon={<MultiplyIcon />} aria-label={removeAriaLabel ?? filename} /> : null}
      </StyledTile>
    </Tooltip>;
};
export default AttachmentTile;